// --- React
import React, { useMemo } from 'react';
// --- Utils
import { getMetadataDetails, MetadataTables } from '../utils/getDetailsData';
// --- Components
import { FieldRow } from '../../components';
// --- Styles
import '../../styles.scss';

/**
 * MetadataCard
 *
 * Features:
 * - Displays metadata details in tap-ui style (horizontal label-value)
 * - Supports search/filter functionality
 * - Includes tables for Cast, Crew, and Foreign Titles
 *
 * @param {object} details - Product details object
 * @param {string} searchTerm - Optional search term to filter fields
 * @returns {Element}
 */
export function MetadataCard({ details, searchTerm = '' }) {
    // Function to extract metadata details from the provided data
    const metadataDetails = useMemo(() => getMetadataDetails(details), [details]);

    // Filter fields based on search term
    const filteredFields = useMemo(() => {
        if (!searchTerm.trim()) return metadataDetails;

        const term = searchTerm.toLowerCase();
        return metadataDetails.filter(
            (field) =>
                field.label.toLowerCase().includes(term) ||
                String(field.value || '').toLowerCase().includes(term)
        );
    }, [metadataDetails, searchTerm]);

    const hasNoResults = filteredFields.length === 0 && searchTerm.trim();

    return (
        <div className="metadata-card">
            {hasNoResults ? (
                <div className="no-results">
                    <i className="fa-solid fa-search" />
                    <p>No fields match your search.</p>
                </div>
            ) : (
                <>
                    <div className="detail-section-content">
                        {filteredFields.map(({ label, value }, idx) => (
                            <FieldRow
                                key={`meta-${idx}`}
                                label={label}
                                value={value}
                                noBorder={idx === filteredFields.length - 1}
                            />
                        ))}
                    </div>

                    {!searchTerm.trim() && (
                        <div className="metadata-tables">
                            <MetadataTables details={details} />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
